import React, { useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash2Icon } from "lucide-react";
import { RichTextEditor } from "@/components/RichTextEditor/RichTextEditor";
import {
  COURSE_CONTENT_BLOCK_TYPES,
  CourseContentBlockType,
} from "@/modules/learningCenter/features/CourseManagement/types/course";
import { CourseFormBlock } from "@/modules/learningCenter/features/CourseManagement/components/CourseForm/types";
import { CalloutBlockEditor } from "@/modules/learningCenter/features/CourseManagement/components/CourseContentBlockEditor/CalloutBlockEditor";
import { ReflectionBlockEditor } from "@/modules/learningCenter/features/CourseManagement/components/CourseContentBlockEditor/ReflectionBlockEditor";
import { KnowledgeCheckBlockEditor } from "@/modules/learningCenter/features/CourseManagement/components/CourseContentBlockEditor/KnowledgeCheckBlockEditor";

interface CourseContentBlockEditorProps {
  blocks: CourseFormBlock[];
  onChange: (blocks: CourseFormBlock[]) => void;
}

const BLOCK_LABELS: Record<CourseContentBlockType, string> = {
  text: "Text",
  image: "Image",
  file: "File",
  video: "Video",
  callout: "Callout",
  reflection: "Reflection",
  knowledge_check: "Knowledge Check",
};

/**
 * Authoring surface for a module's content blocks. Supports the same block set
 * the course backend validates (`CourseContentBlock::TYPES`) and reuses the
 * shared `RichTextEditor` for the rich-text `text` block type.
 */
export function CourseContentBlockEditor({
  blocks,
  onChange,
}: CourseContentBlockEditorProps) {
  const handleAddBlock = useCallback(
    (type: CourseContentBlockType) => {
      const newBlock: CourseFormBlock = {
        id: crypto.randomUUID(),
        uuid: null,
        type,
        content: "",
        metadata: null,
      };
      onChange([...blocks, newBlock]);
    },
    [blocks, onChange],
  );

  const handleRemoveBlock = useCallback(
    (id: string) => {
      onChange(blocks.filter((block) => block.id !== id));
    },
    [blocks, onChange],
  );

  const handleContentChange = useCallback(
    (id: string, content: string) => {
      onChange(
        blocks.map((block) =>
          block.id === id ? { ...block, content } : block,
        ),
      );
    },
    [blocks, onChange],
  );

  const handleMetadataChange = useCallback(
    (id: string, metadata: Record<string, unknown> | null) => {
      onChange(
        blocks.map((block) =>
          block.id === id ? { ...block, metadata } : block,
        ),
      );
    },
    [blocks, onChange],
  );

  const renderBlockEditor = (block: CourseFormBlock) => {
    switch (block.type) {
      case "text":
        return (
          <RichTextEditor
            id={block.id}
            initialContent={block.content ?? ""}
            onChange={(content) => handleContentChange(block.id, content)}
            onRemove={() => handleRemoveBlock(block.id)}
          />
        );

      case "callout":
        return (
          <CalloutBlockEditor
            block={block}
            onContentChange={(content) =>
              handleContentChange(block.id, content)
            }
            onMetadataChange={(metadata) =>
              handleMetadataChange(block.id, metadata)
            }
          />
        );

      case "reflection":
        return (
          <ReflectionBlockEditor
            block={block}
            onMetadataChange={(metadata) =>
              handleMetadataChange(block.id, metadata)
            }
          />
        );

      case "knowledge_check":
        return (
          <KnowledgeCheckBlockEditor
            block={block}
            onMetadataChange={(metadata) =>
              handleMetadataChange(block.id, metadata)
            }
          />
        );

      case "image":
      case "file":
      case "video":
      default:
        return (
          <Input
            onChange={(e) => handleContentChange(block.id, e.target.value)}
            placeholder={
              block.type === "video"
                ? "Enter URL..."
                : "Enter file reference / URL..."
            }
            value={block.content ?? ""}
          />
        );
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {COURSE_CONTENT_BLOCK_TYPES.map((type) => (
          <Button
            key={type}
            onClick={() => handleAddBlock(type)}
            size="sm"
            type="button"
            variant="outline"
          >
            Add {BLOCK_LABELS[type]}
          </Button>
        ))}
      </div>

      {blocks.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No content blocks yet. Add one above.
        </p>
      ) : (
        <div className="space-y-3">
          {blocks.map((block) => (
            <div
              className="rounded-lg border border-gray-200 dark:border-slate-700 p-3 bg-white dark:bg-slate-800"
              key={block.id}
            >
              <div className="mb-2 flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {BLOCK_LABELS[block.type]}
                </span>
                <button
                  aria-label="Remove block"
                  className="text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                  onClick={() => handleRemoveBlock(block.id)}
                  type="button"
                >
                  <Trash2Icon className="h-4 w-4" />
                </button>
              </div>

              {renderBlockEditor(block)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
